/**
 * Enhanced Button component built on Chakra UI
 * Provides consistent styling and behavior across the application
 */

import React from 'react';
import {
  Button as ChakraButton,
  ButtonProps as ChakraButtonProps,
  forwardRef,
  Spinner,
} from '@chakra-ui/react';
import {
  BaseComponentProps,
  ComponentSize,
  ComponentVariant,
} from '../../types';

export interface ButtonProps
  extends BaseComponentProps,
    Omit<ChakraButtonProps, 'size' | 'variant'> {
  /** Button variant/color scheme */
  variant?: ComponentVariant;
  /** Button size */
  size?: ComponentSize;
  /** Whether button is in loading state */
  loading?: boolean;
  /** Loading text to show when loading */
  loadingText?: string;
  /** Whether button should be full width */
  fullWidth?: boolean;
  /** Left icon */
  leftIcon?: React.ReactElement;
  /** Right icon */
  rightIcon?: React.ReactElement;
}

const variantMap: Record<ComponentVariant, string> = {
  primary: 'blue',
  secondary: 'gray',
  success: 'green',
  warning: 'orange',
  danger: 'red',
  info: 'teal',
  outline: 'gray',
  ghost: 'gray',
};

export const Button = forwardRef<ButtonProps, 'button'>((props, ref) => {
  const {
    variant = 'primary',
    size = 'md',
    loading = false,
    loadingText,
    fullWidth = false,
    leftIcon,
    rightIcon,
    children,
    className,
    testId,
    disabled,
    ...rest
  } = props;

  return (
    <ChakraButton
      ref={ref}
      colorScheme={variantMap[variant]}
      size={size}
      isLoading={loading}
      loadingText={loadingText}
      spinner={<Spinner size={size === 'xs' || size === 'sm' ? 'xs' : 'sm'} />}
      width={fullWidth ? 'full' : undefined}
      leftIcon={!loading ? leftIcon : undefined}
      rightIcon={!loading ? rightIcon : undefined}
      isDisabled={disabled || loading}
      className={className}
      data-testid={testId}
      {...rest}
    >
      {children}
    </ChakraButton>
  );
});

Button.displayName = 'Button';
